import React, { useState, useEffect } from 'react';

import { I18nProvider, LOCALES } from '../../i18n';
import useTheme from './hooks/useTheme';
import useSepoliaLive from './hooks/useSepoliaLive';

import Background from './Background';
import Nav from './Nav';
import Hero from './Hero';
import FundingCrisis from './FundingCrisis';
import Vision from './Vision';
import Auction from './Auction';
import ValueLoop from './ValueLoop';
import Benefits from './Benefits';
import Verify from './Verify';
import Roadmap from './Roadmap';
import OpenInvitations from './OpenInvitations';
import Papers from './Papers';
import FAQ from './FAQ';
import Footer from './Footer';

import './landing-v2.css';

/* ============================================================
   LandingV2 — paralllel landing at /v2
   - dark default, light toggle, persisted in localStorage
   - i18n EN/ES/FR via the same IntlProvider as the original Landing
   - mock real-time data (swap-in point for Sepolia hooks later)
   - bg image: public/assets/images/dna_blockchain.jpg
   ============================================================ */

function findInitialLocale() {
  const preferred = (navigator.languages && navigator.languages.length)
    ? navigator.languages
    : [navigator.language || 'en'];
  for (const lang of preferred) {
    const base = (lang || '').toLowerCase().split('-')[0];
    if (base === 'fr') return LOCALES.FRENCH;
    if (base === 'es') return LOCALES.SPANISH;
    if (base === 'en') return LOCALES.ENGLISH;
  }
  return LOCALES.ENGLISH;
}

// Inject Google Fonts once on mount (Space Grotesk + Inter + JetBrains Mono).
// Done via DOM rather than touching public/index.html so /v2 stays self-contained.
function useGoogleFonts() {
  useEffect(() => {
    const id = 'lv2-google-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500;600&display=swap';
    document.head.appendChild(link);
  }, []);
}

export default function LandingV2() {
  const [theme, toggleTheme] = useTheme();
  const [locale, setLocale] = useState(findInitialLocale);
  const live = useSepoliaLive();

  useGoogleFonts();

  return (
    <I18nProvider locale={locale}>
      <div className="lv2-root" data-theme={theme}>
        <Background />

        <Nav
          theme={theme}
          onToggleTheme={toggleTheme}
          locale={locale}
          onChangeLocale={setLocale}
        />

        <Hero live={live} locale={locale} />
        <FundingCrisis />
        <Vision />
        <Auction />
        <ValueLoop />
        <Benefits />
        <Verify />
        <Roadmap />
        <OpenInvitations />
        <Papers />
        <FAQ />
        <Footer />
      </div>
    </I18nProvider>
  );
}
